import {
  CIS_LEVEL_SCOPE_LABEL,
  CisLevelScope,
  COMPLIANCE_STANDARDS,
  roleAtLeast,
  type ComplianceControl,
  type ComplianceFix,
  type ComplianceScope,
  type ComplianceStandard,
  type ComplianceSummary,
  type ComplianceSummaryItem,
  type ComplianceView,
  type ServerRef,
} from '@bartizo/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router';
import {
  getComplianceControlChecks,
  getComplianceStandard,
  getComplianceSummary,
  refreshCompliance,
} from '../api/compliance.js';
import { listServerGroups, listServers } from '../api/servers.js';
import { useCurrentUser } from '../auth/session-context.js';
import { ApplyFixesButton } from '../components/ApplyFixesDialog.js';
import { DecisionsCard, PosturePanel } from '../components/CompliancePosture.js';
import { FixSteps } from '../components/FixSteps.js';
import { GuidanceNote, RecordDecision } from '../components/ComplianceDecisions.js';
import { PageHeader } from '../components/PageHeader.js';
import { complianceProgress } from '../lib/compliance-status.js';
import { fixRunbook } from '../lib/fix-runbook.js';
import { safeFixCount } from '../lib/safe-fixes.js';
import { effectiveTimezone, formatRelative } from '../lib/time.js';
import { Alert } from '../ui/Alert.js';
import { Button } from '../ui/Button.js';
import { Card } from '../ui/Card.js';
import { DataTable, ShareBar, type Column } from '../ui/DataTable.js';
import { SelectField } from '../ui/Field.js';
import { Loading } from '../ui/Skeleton.js';
import { TabPanel, Tabs, type TabItem } from '../ui/Tabs.js';
import { useTab } from '../ui/use-tab.js';

/**
 * Compliance (P11.8.9): the configuration assessment's checks seen through the
 * standards Wazuh's CIS policies tag them with — each standard's score for the
 * fleet, its failing controls with the servers failing each, and what to fix
 * first. Evidence from a benchmark, not an audit.
 */

const ALL_TABS: readonly TabItem<ComplianceStandard>[] = COMPLIANCE_STANDARDS.map((each) => ({
  id: each.id,
  label: each.label,
}));

const scoreBadge = (score: number | null): string =>
  score === null
    ? 'badge-muted'
    : score >= 80
      ? 'badge-success'
      : score >= 50
        ? 'badge-warning'
        : 'badge-danger';

const STATUS: Readonly<Record<ComplianceControl['status'], { label: string; badge: string }>> = {
  failing: { label: 'failing', badge: 'badge-danger' },
  passing: { label: 'passing', badge: 'badge-success' },
  not_applicable: { label: 'not applicable', badge: 'badge-muted' },
};

function Servers({ servers, count }: { servers: readonly ServerRef[]; count: number }): ReactNode {
  return (
    <>
      {servers.map((server, i) => (
        <span key={server.id}>
          {i > 0 && ', '}
          <Link to={`/servers/${encodeURIComponent(server.id)}?tab=hardening`}>{server.name}</Link>
        </span>
      ))}
      {count > servers.length && ` and ${String(count - servers.length)} more`}
    </>
  );
}

/** Which servers the page is of, kept in the address: `?server=` or `?group=`. */
function useScope(): [ComplianceScope, string, (choice: string) => void] {
  const [params, setParams] = useSearchParams();
  const server = params.get('server');
  const group = params.get('group');
  const scope: ComplianceScope = {
    ...(server === null ? {} : { server }),
    ...(server === null && group !== null ? { group } : {}),
  };
  const choice = server !== null ? `server:${server}` : group !== null ? `group:${group}` : 'all';
  const select = (next: string): void => {
    setParams(
      (previous) => {
        const out = new URLSearchParams(previous);
        out.delete('server');
        out.delete('group');
        const [kind, ...rest] = next.split(':');
        if (kind === 'server' || kind === 'group') out.set(kind, rest.join(':'));
        return out;
      },
      { replace: true },
    );
  };
  return [scope, choice, select];
}

/** Which CIS level's checks the page is of (P11.21), kept in the address: `?level=l1`. */
function useLevel(): [CisLevelScope, (level: CisLevelScope) => void] {
  const [params, setParams] = useSearchParams();
  const level = CisLevelScope.catch('all').parse(params.get('level') ?? 'all');
  const select = (next: CisLevelScope): void => {
    setParams(
      (previous) => {
        const out = new URLSearchParams(previous);
        if (next === 'all') out.delete('level');
        else out.set('level', next);
        return out;
      },
      { replace: true },
    );
  };
  return [level, select];
}

function ScopeFilter({
  choice,
  onChange,
  level,
  onLevelChange,
}: {
  choice: string;
  onChange: (choice: string) => void;
  level: CisLevelScope;
  onLevelChange: (level: CisLevelScope) => void;
}): ReactNode {
  const groups = useQuery({ queryKey: ['server-groups'], queryFn: listServerGroups });
  const servers = useQuery({
    queryKey: ['servers', { limit: 100 }],
    queryFn: () => listServers({ limit: 100 }),
  });
  return (
    <div className="toolbar">
      <SelectField
        label="Show"
        value={choice}
        onChange={(event) => {
          onChange(event.target.value);
        }}
      >
        <option value="all">All servers</option>
        {(groups.data ?? []).length > 0 && (
          <optgroup label="A group">
            {(groups.data ?? []).map((group) => (
              <option key={group.id} value={`group:${group.id}`}>
                {group.name}
              </option>
            ))}
          </optgroup>
        )}
        <optgroup label="One server">
          {(servers.data?.items ?? []).map((server) => (
            <option key={server.id} value={`server:${server.id}`}>
              {server.name}
            </option>
          ))}
        </optgroup>
      </SelectField>
      <SelectField
        label="Checks"
        hint="CIS Level 1 is the baseline for a server; Level 2 adds the stricter ones."
        value={level}
        onChange={(event) => {
          onLevelChange(CisLevelScope.parse(event.target.value));
        }}
      >
        {CisLevelScope.options.map((each) => (
          <option key={each} value={each}>
            {CIS_LEVEL_SCOPE_LABEL[each]}
          </option>
        ))}
      </SelectField>
    </div>
  );
}

/**
 * How fresh the numbers are (P11.19): the scan, when Bartizo last read it, where
 * the last fixes are on the way to showing, and a button to read now.
 */
function AssessmentStatus({
  summary,
  scope,
  level,
}: {
  summary: ComplianceSummary;
  scope: ComplianceScope;
  level: CisLevelScope;
}): ReactNode {
  const canRefresh = roleAtLeast(useCurrentUser().user.role, 'operator');
  const queryClient = useQueryClient();
  const refresh = useMutation({
    mutationFn: () => refreshCompliance(scope),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['compliance'] }),
  });
  const { status } = summary;
  const progress = complianceProgress(summary);
  const readMinutes = Math.max(1, Math.round(status.readEverySeconds / 60));
  const readEvery = readMinutes === 1 ? 'minute' : `${String(readMinutes)} minutes`;

  return (
    <div className="stack">
      <p className="muted text-sm">
        {summary.servers === 1
          ? '1 server assessed'
          : `${String(summary.servers)} servers assessed`}
        {summary.scannedAt !== null &&
          `, the latest assessment ${formatRelative(summary.scannedAt)}`}
        {status.readAt !== null && `. Bartizo last read Wazuh ${formatRelative(status.readAt)}`}
        {status.lastApply !== null &&
          `. Fixes last applied ${formatRelative(status.lastApply.at)}${status.lastApply.failed ? ', and some failed' : ''}`}
        .
      </p>
      {level !== 'all' && (
        <Alert tone="info" title={`Showing ${CIS_LEVEL_SCOPE_LABEL[level]} checks`}>
          {` The score, controls and fix-first list below are of those checks only. A check whose CIS level is not known stays in, so nothing drops out unseen: the level is known for ${String(summary.levels.known)} of ${String(summary.levels.total)} CIS checks here.`}
        </Alert>
      )}
      {progress.phase === 'waiting' && (
        <Alert tone="info" title="Waiting for the rescan after the fixes">
          {` The Wazuh agent scans again within about a minute of restarting, and Bartizo reads Wazuh every ${readEvery}${progress.nextReadAt === null ? '' : `; the next read is ${nextRead(progress.nextReadAt)}`}. The scores below are from the scan before the fixes.`}
        </Alert>
      )}
      {progress.phase === 'stalled' && (
        <Alert tone="warning" title="No scan has arrived since the fixes">
          {' '}
          It has been over half an hour, and a scan takes seconds. The Wazuh agent may not have
          restarted on the server, or it cannot reach the central server. Refresh to read again, and
          check <code>/var/ossec/logs/ossec.log</code> there.
        </Alert>
      )}
      {progress.phase === 'current' && (
        <Alert tone="success">These scores include a scan taken after the last fixes.</Alert>
      )}
      {canRefresh && (
        <div className="toolbar">
          <Button
            busy={refresh.isPending}
            busyLabel="Reading Wazuh"
            onClick={() => {
              refresh.mutate();
            }}
          >
            Refresh now
          </Button>
          {refresh.isSuccess && (
            <span className="muted text-sm" role="status">
              {refresh.data.servers === 0
                ? 'Read a moment ago; nothing newer to fetch.'
                : refresh.data.policiesRead === 0
                  ? 'Read Wazuh: no newer scan yet.'
                  : `Read Wazuh: ${String(refresh.data.policiesRead)} new ${refresh.data.policiesRead === 1 ? 'scan' : 'scans'} stored.`}
            </span>
          )}
          {refresh.isError && (
            <span className="text-sm" role="alert">
              {refresh.error instanceof Error ? refresh.error.message : 'Could not read Wazuh.'}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

/** "in 6 minutes", or "due now" once the time has passed. */
const nextRead = (iso: string): string =>
  Date.parse(iso) <= Date.now() ? 'due now' : formatRelative(iso);

export function CompliancePage(): ReactNode {
  const [scope, choice, selectScope] = useScope();
  const [level, selectLevel] = useLevel();
  // What is read: the servers and the CIS level. What is acted on (a decision, a
  // fix, a refresh) is of servers only, so `scope` stays without the level.
  const view: ComplianceView = { ...scope, ...(level === 'all' ? {} : { level }) };
  const session = useCurrentUser();
  const timezone = effectiveTimezone(session.user.timezone, session.instanceTimezone);
  const summary = useQuery({
    queryKey: ['compliance', view],
    queryFn: () => getComplianceSummary(view),
  });
  const present = summary.data?.standards ?? [];
  const tabs =
    present.length === 0
      ? ALL_TABS
      : present.map((each) => ({ id: each.standard, label: each.label }));
  const [standard, select] = useTab(tabs, 'standard');
  const groups = useQuery({ queryKey: ['server-groups'], queryFn: listServerGroups });
  const servers = useQuery({
    queryKey: ['servers', { limit: 100 }],
    queryFn: () => listServers({ limit: 100 }),
  });
  const [kind = 'all', ...named] = choice.split(':');
  const id = named.join(':');
  const scopeName =
    kind === 'group'
      ? `group ${groups.data?.find((group) => group.id === id)?.name ?? id}`
      : kind === 'server'
        ? `server ${servers.data?.items.find((server) => server.id === id)?.name ?? id}`
        : 'all servers';

  return (
    <div className="page">
      <PageHeader
        title="Compliance"
        description="Each server's configuration assessment (the CIS benchmark Wazuh runs) through the standards its checks are mapped to. A control fails on a server when any of its checks fails there. This is evidence from a benchmark, not an audit: a standard asks for far more than a server's configuration shows."
      />
      <ScopeFilter
        choice={choice}
        onChange={selectScope}
        level={level}
        onLevelChange={selectLevel}
      />
      {summary.isPending ? (
        <Loading />
      ) : summary.isError ? (
        <Alert tone="danger" title="Could not load compliance">
          {summary.error instanceof Error ? summary.error.message : 'Try again in a moment.'}
        </Alert>
      ) : present.length === 0 ? (
        <Card title="Nothing assessed yet">
          <p className="muted text-sm">
            {choice === 'all'
              ? "Compliance comes from each server's configuration assessment, which the central server reads from Wazuh every ten minutes once a server's Wazuh agent reports to it."
              : kind === 'server'
                ? "There is no score to show because nothing has been assessed on this server, so no check has passed or failed. A score appears within about ten minutes of its Wazuh agent's first scan. The central server's own host is not assessed unless a Bartizo agent runs on it."
                : 'None of the servers shown has an assessment yet. Pick another, or all servers.'}
          </p>
          {kind === 'server' && (
            <p className="text-sm">
              <Link to={`/servers/${encodeURIComponent(id)}?tab=hardening`}>
                See this server's Wazuh agent
              </Link>
            </p>
          )}
        </Card>
      ) : (
        <>
          <dl className="stat-tiles">
            {present.slice(0, 4).map((each) => (
              <StandardTile key={each.standard} item={each} onSelect={select} />
            ))}
          </dl>
          <AssessmentStatus summary={summary.data} scope={scope} level={level} />
          <Tabs
            label="Standards"
            tabs={tabs}
            current={standard}
            onSelect={select}
            idPrefix="compliance"
          />
          <TabPanel idPrefix="compliance" current={standard}>
            <StandardView
              standard={standard}
              scope={scope}
              view={view}
              scopeName={scopeName}
              item={present.find((each) => each.standard === standard)}
            />
          </TabPanel>
          <DecisionsCard scope={scope} timezone={timezone} />
        </>
      )}
    </div>
  );
}

function StandardTile({
  item,
  onSelect,
}: {
  item: ComplianceSummaryItem;
  onSelect: (standard: ComplianceStandard) => void;
}): ReactNode {
  return (
    <div className={`stat-tile ${item.controls.failing > 0 ? 'stat-tile-alert' : ''}`}>
      <dt>
        <button
          type="button"
          className="link-button"
          onClick={() => {
            onSelect(item.standard);
          }}
        >
          {item.label}
        </button>
      </dt>
      <dd>{item.score === null ? '—' : `${String(item.score)}%`}</dd>
      <dd className="cell-sub">
        {item.controls.failing} failing controls · {item.servers.failing} of {item.servers.assessed}{' '}
        servers
      </dd>
    </div>
  );
}

function StandardView({
  standard,
  scope,
  view,
  scopeName,
  item,
}: {
  standard: ComplianceStandard;
  scope: ComplianceScope;
  view: ComplianceView;
  scopeName: string;
  item: ComplianceSummaryItem | undefined;
}): ReactNode {
  const isAdmin = useCurrentUser().user.role === 'admin';
  const loaded = useQuery({
    queryKey: ['compliance', standard, view],
    queryFn: () => getComplianceStandard(standard, view),
  });
  if (loaded.isPending) return <Loading />;
  if (loaded.isError) {
    return (
      <Alert tone="danger" title="Could not load this standard">
        {loaded.error instanceof Error ? loaded.error.message : 'Try again in a moment.'}
      </Alert>
    );
  }
  const data = loaded.data;
  const failing = data.controls.filter((each) => each.status === 'failing');

  const controlColumns: Column<ComplianceControl>[] = [
    {
      key: 'control',
      header: 'Control',
      rowHeader: true,
      cell: (row) => (
        <>
          <span className="mono">{row.control}</span>
          {row.policyId !== null && <span className="cell-sub">{row.policyId}</span>}
          {row.titles[0] !== undefined && (
            <span className="cell-sub control-about" title={row.titles.join('\n')}>
              {row.titles[0]}
              {row.checks > 1 && ` · +${String(row.checks - 1)} more`}
            </span>
          )}
        </>
      ),
      sortValue: (row) => row.control,
    },
    {
      key: 'status',
      header: 'Status',
      cell: (row) => (
        <span className={`badge ${STATUS[row.status].badge}`}>{STATUS[row.status].label}</span>
      ),
      sortValue: (row) => ['failing', 'passing', 'not_applicable'].indexOf(row.status),
    },
    {
      key: 'failing',
      header: 'Failing on',
      cell: (row) =>
        row.failingServerCount === 0 ? (
          <span className="muted">no server</span>
        ) : (
          <Servers servers={row.failingServers} count={row.failingServerCount} />
        ),
      sortValue: (row) => row.failingServerCount,
    },
    {
      key: 'passing',
      header: 'Passing on',
      align: 'end',
      wideOnly: true,
      cell: (row) => row.passingServerCount,
      sortValue: (row) => row.passingServerCount,
    },
    {
      key: 'checks',
      header: 'Checks',
      align: 'end',
      wideOnly: true,
      cell: (row) => row.checks,
      sortValue: (row) => row.checks,
    },
  ];

  const serverColumns: Column<(typeof data.servers)[number]>[] = [
    {
      key: 'server',
      header: 'Server',
      rowHeader: true,
      cell: (row) => (
        <Link to={`/servers/${encodeURIComponent(row.id)}?tab=hardening`}>{row.name}</Link>
      ),
      sortValue: (row) => row.name,
    },
    {
      key: 'score',
      header: 'Score',
      align: 'end',
      cell: (row) => (
        <span className={`badge ${scoreBadge(row.score)}`}>
          {row.score === null ? '—' : `${String(row.score)}%`}
        </span>
      ),
      sortValue: (row) => row.score ?? -1,
    },
    {
      key: 'failing',
      header: 'Failing controls',
      align: 'end',
      cell: (row) => row.failing,
      sortValue: (row) => row.failing,
    },
    {
      key: 'passing',
      header: 'Passing controls',
      align: 'end',
      wideOnly: true,
      cell: (row) => row.passing,
      sortValue: (row) => row.passing,
    },
  ];

  return (
    <>
      {item !== undefined && <PosturePanel item={item} />}
      <Card
        title={data.label}
        description={`${String(failing.length)} of ${String(data.controls.filter((each) => each.status !== 'not_applicable').length)} controls fail on at least one server: a control fails where any of its checks does. The score is the passing share of its checks that could be judged, on every server, as Wazuh scores a policy.`}
        aside={
          <span className={`badge ${scoreBadge(data.score)}`}>
            Score {data.score === null ? '—' : `${String(data.score)}%`}
          </span>
        }
      >
        <ShareBar
          value={data.controls.filter((each) => each.status === 'passing').length}
          total={data.controls.filter((each) => each.status !== 'not_applicable').length}
        />
        <p className="cell-sub">Controls passing on every server judged.</p>
      </Card>

      <Card
        title="Fix first"
        description="The failing checks whose fix does most: the servers failing each, times the controls of this standard it maps to."
        {...(data.fixFirst.length > 0
          ? {
              aside: (
                <RunbookButtons
                  fixes={data.fixFirst}
                  standard={data.label}
                  scope={scope}
                  scopeName={scopeName}
                />
              ),
            }
          : {})}
      >
        {data.fixFirst.length === 0 ? (
          <p className="muted text-sm">No check of this standard fails.</p>
        ) : (
          <ol className="check-list">
            {data.fixFirst.map((fix) => (
              <li key={`${fix.policyId}|${String(fix.checkId)}`}>
                <details className="check-row">
                  <summary>
                    <span>{fix.title}</span>
                    <span className="cell-sub">
                      {fix.serverCount === 1 ? '1 server' : `${String(fix.serverCount)} servers`}
                      {' · '}
                      {fix.controls.length === 1 ? 'control' : 'controls'} {fix.controls.join(', ')}
                    </span>
                  </summary>
                  {fix.rationale !== null && (
                    <section>
                      <h4>Why it matters</h4>
                      <p className="text-sm">{fix.rationale}</p>
                    </section>
                  )}
                  {fix.remediation !== null && (
                    <section>
                      <h4>How to fix it</h4>
                      <FixSteps title={fix.title} remediation={fix.remediation} />
                    </section>
                  )}
                  <GuidanceNote title={fix.title} />
                  <section>
                    <h4>Failing on</h4>
                    <p className="text-sm">
                      <Servers servers={fix.servers} count={fix.serverCount} />
                    </p>
                  </section>
                  {isAdmin && (
                    <RecordDecision
                      target={{ policyId: fix.policyId, checkId: fix.checkId, title: fix.title }}
                      scope={scope}
                      scopeName={scopeName}
                    />
                  )}
                </details>
              </li>
            ))}
          </ol>
        )}
      </Card>

      <Card title="Controls" description="Failing first, the most servers first.">
        <DataTable
          caption={`${data.label} controls`}
          columns={controlColumns}
          rows={data.controls}
          rowKey={(row) => `${row.policyId ?? ''}|${row.control}`}
          renderDetail={(row) => (
            <ControlChecks
              standard={standard}
              control={row}
              scope={scope}
              view={view}
              scopeName={scopeName}
            />
          )}
          pagination={{}}
          empty={<p className="muted text-sm">No check is mapped to this standard.</p>}
        />
      </Card>

      <Card title="Servers" description="Lowest score first.">
        <DataTable
          caption={`Servers by ${data.label} score`}
          columns={serverColumns}
          rows={data.servers}
          rowKey={(row) => row.id}
          empty={<p className="muted text-sm">No server is assessed against it.</p>}
        />
      </Card>
    </>
  );
}

/** The runbook of the fixes listed, copied or saved: a file to read and run by hand. */
function RunbookButtons({
  fixes,
  standard,
  scope,
  scopeName,
}: {
  fixes: readonly ComplianceFix[];
  standard: string;
  scope: ComplianceScope;
  scopeName: string;
}): ReactNode {
  const isAdmin = useCurrentUser().user.role === 'admin';
  const [copied, setCopied] = useState(false);
  const text = (): string => fixRunbook({ standard, scope: scopeName, fixes, now: new Date() });
  const own = safeFixCount(fixes.map((fix) => fix.title));
  return (
    <span className="row">
      <span
        className="muted text-sm"
        title="The rest are described in the file as comments: a firewall, mount or bootloader change can lock a server out, so a person makes those."
      >
        {own} of {fixes.length} run by the script
      </span>
      <Button
        onClick={() => {
          void navigator.clipboard
            .writeText(text())
            .then(() => {
              setCopied(true);
            })
            .catch(() => undefined);
        }}
      >
        {copied ? 'Copied' : 'Copy fix script'}
      </Button>
      <Button
        onClick={() => {
          const url = URL.createObjectURL(new Blob([text()], { type: 'text/x-shellscript' }));
          const link = document.createElement('a');
          link.href = url;
          link.download = `bartizo-fixes-${standard.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}.sh`;
          link.click();
          URL.revokeObjectURL(url);
        }}
      >
        Download .sh
      </Button>
      {isAdmin && <ApplyFixesButton fixes={fixes} scope={scope} scopeName={scopeName} />}
    </span>
  );
}

/** What one control's checks ask, why, how to fix them, and where each fails. */
function ControlChecks({
  standard,
  control,
  scope,
  view,
  scopeName,
}: {
  standard: ComplianceStandard;
  control: ComplianceControl;
  scope: ComplianceScope;
  view: ComplianceView;
  scopeName: string;
}): ReactNode {
  const isAdmin = useCurrentUser().user.role === 'admin';
  const found = useQuery({
    queryKey: ['compliance-checks', standard, control.policyId, control.control, view],
    queryFn: () => getComplianceControlChecks(standard, control, view),
  });
  if (found.isPending) return <Loading />;
  if (found.isError) {
    return (
      <Alert tone="danger" title="Could not load this control's checks">
        {found.error instanceof Error ? found.error.message : 'Try again in a moment.'}
      </Alert>
    );
  }
  return (
    <ol className="check-list">
      {found.data.checks.map((check) => (
        <li key={`${check.policyId}|${String(check.checkId)}`}>
          <details
            className="check-row"
            open={check.status === 'failing' && found.data.checks.length <= 3}
          >
            <summary>
              <span className={`badge ${STATUS[check.status].badge}`}>
                {STATUS[check.status].label}
              </span>{' '}
              <span>{check.title}</span>
              {check.failingServerCount > 0 && (
                <span className="cell-sub">
                  Failing on{' '}
                  <Servers servers={check.failingServers} count={check.failingServerCount} />
                </span>
              )}
            </summary>
            {check.rationale !== null && (
              <section>
                <h4>Why it matters</h4>
                <p className="text-sm">{check.rationale}</p>
              </section>
            )}
            {check.remediation !== null && (
              <section>
                <h4>How to fix it</h4>
                <FixSteps title={check.title} remediation={check.remediation} />
              </section>
            )}
            {check.status === 'failing' && <GuidanceNote title={check.title} />}
            {check.status === 'failing' && isAdmin && (
              <RecordDecision
                target={{ policyId: check.policyId, checkId: check.checkId, title: check.title }}
                scope={scope}
                scopeName={scopeName}
              />
            )}
          </details>
        </li>
      ))}
    </ol>
  );
}
